import { useState } from "react";
import { api, errorMessage, newIdempotencyKey, unwrap, type EventData, type PoiCategory } from "../../api/client";
import { fr } from "../../i18n/fr";
import { Confirm } from "../../ui/components";
import { CategoryFields, PALETTE } from "./CategoryFields";

/** POI categories of the event (SW-WS-14): name, colour, pictogram, visibility in the vehicles. */
export function CategoriesPanel({ eventId, event: e, readOnly, onChanged }: { eventId: string; event: EventData; readOnly: boolean; onChanged(msg?: string): Promise<void> }) {
  const [editing, setEditing] = useState<PoiCategory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState<PoiCategory | null>(null);
  const [idemKey, setIdemKey] = useState(newIdempotencyKey);
  const cats = e.poiCategories ?? [];
  const counts = new globalThis.Map<string, number>();
  for (const p of e.pois ?? []) counts.set(p.categoryId, (counts.get(p.categoryId) ?? 0) + 1);

  const save = async () => {
    if (!editing) return;
    setBusy(true); setError(null);
    try {
      if (editing.id) unwrap(await api.PATCH("/events/{id}/categories/{cid}", { params: { path: { id: eventId, cid: editing.id } }, body: editing }));
      else unwrap(await api.POST("/events/{id}/categories", { params: { path: { id: eventId } }, body: editing, headers: { "Idempotency-Key": idemKey } }));
      setEditing(null); setIdemKey(newIdempotencyKey());
      await onChanged();
    } catch (x) { setError(errorMessage(x, fr.errors)); } finally { setBusy(false); }
  };

  const del = async () => {
    if (!toDelete) return;
    setBusy(true); setError(null);
    try {
      unwrap(await api.DELETE("/events/{id}/categories/{cid}", { params: { path: { id: eventId, cid: toDelete.id } } }));
      setToDelete(null);
      await onChanged();
    } catch (x) { setError(errorMessage(x, fr.errors)); setToDelete(null); } finally { setBusy(false); }
  };

  return (
    <section className="stack">
      <div className="row between"><h3>{fr.categories.title}</h3>
        {!readOnly && !editing && <button className="primary" onClick={() => setEditing({ id: "", name: "", color: PALETTE[cats.length % PALETTE.length], icon: "pin", hidden: false, sortOrder: cats.length })}>{fr.categories.new}</button>}
      </div>
      {error && <div className="error small">{error}</div>}
      {editing && (
        <form className="card stack form" onSubmit={(ev) => { ev.preventDefault(); void save(); }}>
          <CategoryFields value={editing} onChange={(v) => setEditing(v)} />
          <label className="check"><input type="checkbox" checked={editing.hidden} onChange={(ev) => setEditing({ ...editing, hidden: ev.target.checked })} /> {fr.categories.hidden}</label>
          <div className="row end">
            <button type="button" onClick={() => setEditing(null)}>{fr.common.cancel}</button>
            <button type="submit" className="primary" disabled={busy || !editing.name.trim()}>{busy ? fr.common.saving : fr.common.save}</button>
          </div>
        </form>
      )}
      {cats.length === 0 && !editing && <p className="muted small">{fr.poiForm.noCategory}</p>}
      <ul className="object-list">
        {cats.map((c) => (
          <li key={c.id} className={c.hidden ? "disabled" : ""}>
            <span className="kind-dot poi" style={{ background: c.color ?? "#2f9e44" }} />
            <span className="obj-main">{c.name}</span>
            <span className="muted small">{fr.categories.icons[c.icon ?? ""] ?? c.icon} · {fr.categories.count(counts.get(c.id) ?? 0)}{c.hidden ? ` · ${fr.categories.hidden.toLowerCase()}` : ""}</span>
            {!readOnly && (
              <span className="row">
                <button className="ghost" onClick={() => setEditing(c)}>{fr.common.edit}</button>
                <button className="ghost danger" disabled={(counts.get(c.id) ?? 0) > 0} title={(counts.get(c.id) ?? 0) > 0 ? fr.categories.inUse : ""} onClick={() => setToDelete(c)}>{fr.common.delete}</button>
              </span>
            )}
          </li>
        ))}
      </ul>
      <Confirm open={!!toDelete} title={fr.common.delete} text={fr.categories.deleteConfirm} danger confirmLabel={fr.common.delete} onConfirm={del} onClose={() => setToDelete(null)} />
    </section>
  );
}
